import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import type { Locale } from "@/types"
import type { CollabDetail } from "@/types/collab"

import { AddressBlock } from "./address-block"
import { ProductBlock } from "./product-block"
import { BrandChoiceLine, ProductChoiceBlock } from "./product-choice-block"
import { TrackingBlock } from "./tracking-block"
import { VoidPossessionDialog } from "./void-possession-dialog"

/**
 * OKS-482 — The product/shipping (gifting) branch of the §08 mission-detail
 * sheet, for drop + promotional missions. Replaces the brief/requirements/
 * community body while the collab is in a gifting state; the page's existing
 * `CTAButton` (driven by `collab.cta`) renders the state-specific primary
 * action below it (confirm_address / preparing-disabled / shipped-disabled /
 * confirm_receipt / submit-picker), so this section owns ONLY the badge +
 * heading + subtitle + the product/address/tracking blocks.
 *
 * The five derived views map to (state, flag):
 *   confirm-address  to_ship   & !address_confirmed
 *   preparing        to_ship   &  address_confirmed
 *   shipped          shipped
 *   delivered        delivered & !received
 *   received         delivered &  received
 */
export type GiftingView =
  | "confirm_address"
  // OKS-1040 (PO 3.6 D3, UX §2.1) — `to_ship` with the product choice still
  // outstanding and the address already confirmed. Without it the header said
  // « {brand} prépare votre produit » under a CTA bar saying the brand waits
  // on the creator's choice — two facts that cannot both be true.
  | "choose_product"
  | "preparing"
  | "shipped"
  | "delivered"
  | "received"
  // OKS-936 — a LIVE no-ship collab. Reaches `delivered` without anything
  // having been shipped, so the whole shipping sub-block is REPLACED (not
  // hidden with gaps): no address card, no tracking line, no delivery ETA.
  | "no_ship"

const GIFTING_STATES = new Set(["to_ship", "shipped", "delivered"])

/**
 * True when the collab should render the gifting branch. "Gifting" requires a
 * structured product on the wire (`collab.product`) — the backend only emits
 * `product` for a mission with a `MissionProduct` whose drop has fired
 * (`CardSerializer#gift_visible?`). Without one (pre-reveal, or a product-less
 * drop/promotional) the standard detail body renders, and the ADDRESS flow
 * reaches the creator through `isPreRevealAddressCollab` instead (OKS-1002,
 * OKS-1039).
 */
export function isGiftingCollab(collab: CollabDetail): boolean {
  const type = collab.mission.type
  return (
    (type === "drop" || type === "promotional") &&
    GIFTING_STATES.has(collab.state) &&
    // OKS-1040 — an outstanding product choice carries NO `product` (naming
    // product #1 there was the defect), but it is still a gifting collab: the
    // Choose block and the address block render in this section.
    (collab.product != null || collab.product_choice_required === true)
  )
}

/**
 * OKS-1002 (D4) — true when the PRE-REVEAL address section should render: a
 * `to_ship` collab whose gift is still withheld by `gift_visible?`, where the
 * creator's address is nonetheless the thing holding the parcel up.
 *
 * The rule this encodes: **the drop reveal adds the product; it must not add
 * or remove the address.** Before this, `isGiftingCollab` required
 * `collab.product`, so the whole gifting section — address block included —
 * was absent before `drops_at`, while the sticky bar still offered the
 * « Confirmer l'adresse » POST. The creator was asked to confirm an address
 * they could not see, and the server accepted it.
 *
 * ⚠️ IT DELIBERATELY DOES NOT ASK "does this mission carry a gift product".
 * That fact is NOT derivable from the wire: `product` is absent both before
 * `drops_at` and on a product-less gifting mission, and since OKS-1039 BOTH
 * owe an address. The two flags below are the server's answer to the actual
 * question — "is there a parcel whose destination the creator owns" — derived
 * from `Collab#address_revealable?`, the same window the brand queue reads.
 *
 * ⚠️ `state === "to_ship"` is load-bearing, not belt-and-braces.
 * `address_confirmed` stays true after the state moves on, and this predicate
 * is consulted from a branch every non-gifting collab reaches — without the
 * clause, a pre-reveal `shipped` or `delivered` collab (a no-ship collab
 * reaches `delivered` without any reveal) would render an address section for
 * a parcel that has already gone.
 *
 * ⚠️ Mutual exclusivity with `GiftingSection` is STRUCTURAL, not enforced
 * here: the caller renders this inside the ELSE branch of
 * `isGiftingCollab(collab) ? … : …`. Do not add a re-derivation of that gate
 * to this predicate — two expressions of one condition is how they drift.
 */
export function isPreRevealAddressCollab(collab: CollabDetail): boolean {
  const type = collab.mission.type
  if (type !== "drop" && type !== "promotional") return false
  if (collab.state !== "to_ship") return false

  return collab.address_action_required || collab.address_confirmed === true
}

/** Derives the current gifting view from the collab state + flags. */
export function giftingViewFor(collab: CollabDetail): GiftingView {
  switch (collab.state) {
    case "to_ship":
      // OKS-1040 (UX §2.1) — precedence: an outstanding choice over a
      // confirmed address; an unconfirmed address keeps `confirm_address`
      // (its waiting notice and phone chase are unchanged).
      if (collab.product_choice_required && collab.address_confirmed)
        return "choose_product"
      return collab.address_confirmed ? "preparing" : "confirm_address"
    case "shipped":
      return "shipped"
    case "delivered":
      // OKS-936 — checked BEFORE `received`, mirroring
      // `Collabs::CtaResolver#delivered_cta`. A live no-ship collab has
      // `received == false` by construction (`Collabs::ConfirmFlag` refuses
      // the flag), so the order is belt-and-braces — but it keeps the FE
      // branch reading the same way as the server's.
      if (collab.no_ship) return "no_ship"
      return collab.received ? "received" : "delivered"
    default:
      // Unreachable when gated by `isGiftingCollab`; default keeps TS happy.
      return "preparing"
  }
}

interface GiftingSectionProps {
  collab: CollabDetail
  /** `mission.product_info` fallback caption for the product block. */
  productInfo: string | null
  locale: Locale
}

/**
 * Per-view badge i18n key + tone. `badgeKey` doubles as the `data-testid`
 * suffix (`gifting-badge-{badgeKey}`) so the testid is underscore-based and
 * matches the Playwright/Vitest contract — NEVER hand-derive a separate
 * hyphenated id (that drift caused the OKS-482 E1 CI red). The `received`
 * view reuses the `delivered` badge (green "Delivered 🎉").
 */
const VIEW_META: Record<
  GiftingView,
  { badgeKey: string; variant: "secondary" | "default" }
> = {
  confirm_address: { badgeKey: "confirm_address", variant: "secondary" },
  choose_product: { badgeKey: "choose_product", variant: "secondary" },
  preparing: { badgeKey: "preparing", variant: "secondary" },
  shipped: { badgeKey: "shipped", variant: "default" },
  delivered: { badgeKey: "delivered", variant: "default" },
  received: { badgeKey: "delivered", variant: "default" },
  no_ship: { badgeKey: "no_ship", variant: "secondary" },
}

export function GiftingSection({
  collab,
  productInfo,
  locale,
}: GiftingSectionProps) {
  const { t } = useTranslation()
  const view = giftingViewFor(collab)
  const meta = VIEW_META[view]
  const productName =
    collab.product?.name ?? t("creator_mission_detail.gifting.your_product")

  return (
    <section
      className="flex flex-col gap-4"
      data-testid="gifting-section"
      data-gifting-view={view}
    >
      <div className="flex flex-col gap-1.5">
        <Badge
          variant={meta.variant}
          className="self-start"
          data-testid={`gifting-badge-${meta.badgeKey}`}
        >
          {t(`creator_mission_detail.gifting.badge.${meta.badgeKey}`)}
        </Badge>
        {/*
          OKS-936 — the no-ship headline is "Use the {product} you already
          have". `productName` is `mission_products.name`, the SNAPSHOT —
          ⚠️ NEVER `products.title`: the snapshot is NOT NULL while a
          LEFT JOIN products yields NULL for a deleted product. This exact
          "improvement" was proposed and retracted on OKS-935.

          `break-words` and no truncation (UX §5): product names are
          unbounded and the name is the whole point of the sentence.
        */}
        <h2
          className="text-base font-semibold break-words"
          data-testid="gifting-heading"
        >
          {view === "no_ship"
            ? t("collabs.no_ship.headline", { product: productName })
            : t(`creator_mission_detail.gifting.heading.${view}`, {
                product: productName,
              })}
        </h2>
        <p className="text-muted-foreground text-sm break-words">
          {view === "no_ship"
            ? t("collabs.no_ship.subhead")
            : t(`creator_mission_detail.gifting.subtitle.${view}`, {
                product: productName,
                brand: collab.brand.name,
              })}
        </p>
      </div>

      {/*
        OKS-936 (UX §3.2) — after the hatch the collab is an ordinary
        `to_ship` gifting collab and renders the EXISTING OKS-482 address
        block; this notice is the only addition. It persists for the rest of
        the collab's life: it costs nothing once the address is confirmed and
        it is the only trace the creator has that their tap landed (the D7
        tuple deliberately cleared `delivered_at`).

        `role="status"` (polite) per UX §6 — and deliberately NOT a second
        `aria-live` region on the assign preview, which already sits inside
        the header's live region.
      */}
      {collab.no_ship_voided && (
        <Alert role="status" data-testid="collab-void-possession-notice">
          <AlertDescription>
            {t("collabs.no_ship.voided_notice", { brand: collab.brand.name })}
          </AlertDescription>
        </Alert>
      )}

      {/* OKS-1040 (UX §2.1) — the Choose block comes FIRST while the choice is
          outstanding, and replaces the product block (there is no product to
          show yet). Not a gate: the address block below stays live (R1b). */}
      {collab.product_choice_required ? (
        <ProductChoiceBlock collab={collab} locale={locale} />
      ) : (
        <>
          <ProductBlock
            product={collab.product}
            captionFallback={productInfo}
            locale={locale}
          />
          {/* OKS-1040 — a brand-made pick: provenance + « Modifier » (R3b). */}
          <BrandChoiceLine collab={collab} locale={locale} />
        </>
      )}

      {/*
        The escape hatch. Rendered iff the SERVER says so — never re-derived
        from state/flags here. Absent (not `aria-disabled`) when unavailable,
        per UX §6.
      */}
      {collab.can_void_possession && (
        <VoidPossessionDialog
          collabId={collab.id}
          productName={productName}
          brandName={collab.brand.name}
          offer={collab.offer}
        />
      )}

      {/*
        OKS-984 (UX §2.4) — the brand is waiting on this address.

        Rendered on `confirm_address` ONLY: on `preparing` the creator has
        already confirmed and there is nothing to chase. `default` variant,
        not `destructive` — this is a nudge, not a failure, and the creator
        has done nothing wrong.

        ⚠️ `address_reminder_sent_at` comes from the SERVER. The alert is not
        conditioned on it: the lead line shows whenever the brand is waiting,
        and the reminder stamp only escalates the wording. A creator who
        reaches this view without a reminder still needs to know someone is
        waiting on them — that is the whole drop-off point this ticket
        exists to close.

        `role="status"` (polite) matching the void-possession notice above.
      */}
      {view === "confirm_address" && (
        <Alert role="status" data-testid="gifting-address-waiting-notice">
          <AlertDescription>
            {collab.address_reminder_sent_at
              ? t("creator_mission_detail.gifting.address_reminded", {
                  brand: collab.brand.name,
                })
              : t("creator_mission_detail.gifting.address_waiting", {
                  brand: collab.brand.name,
                })}
          </AlertDescription>
        </Alert>
      )}

      {/* Address block on confirm-address + preparing — EDITABLE on both.
          ⚠️ This comment used to call `preparing` locked; OKS-994 R1 made it
          correctable and the description was never updated. Corrected because
          a comment that no longer matches the code two lines below it is read
          as the contract by the next editor. */}
      {(view === "confirm_address" ||
        view === "preparing" ||
        view === "choose_product") && (
        <AddressBlock
          collabId={collab.id}
          address={collab.address}
          accountPhone={collab.account_phone}
          // OKS-994 R1 — correctable until it ships, on BOTH views.
          //
          // ⚠️ Unconditional, and it must LOOK unconditional. This was written
          // as `view === "confirm_address" || view === "preparing"` — byte-
          // identical to the guard two lines above, so inside this block it
          // could only ever be `true`. Behaviour was correct; the risk is that
          // it READS as a live gate, so the next author adds a real condition
          // beside it that silently does nothing.
          //
          // The prop is genuinely observed, not decorative: forcing it false
          // reds three arms in `gifting-section.test.tsx`.
          editable
          // OKS-1036 — server-derived pair; see the prop docstring.
          autoConfirmed={
            collab.address_confirmation_source === "profile_auto" &&
            !collab.address_edited_since_confirm
          }
          // OKS-1037 — server-derived; see the prop docstring.
          confirmedByBrandName={
            collab.address_confirmation_source === "brand"
              ? (collab.address_confirmed_by_brand_name ?? null)
              : null
          }
          // The phone chase stays confirm-address-only (UXQ-5).
          promptMissingPhone={view === "confirm_address"}
        />
      )}

      {/* Tracking block only on the shipped state. */}
      {view === "shipped" && (
        <TrackingBlock
          trackingNumber={collab.tracking_number}
          trackingUrl={collab.tracking_url}
          deliveryEta={collab.delivery_eta}
          locale={locale}
        />
      )}
    </section>
  )
}
